import clsx from "clsx";
import { AlertTriangle, CheckCircle2, ChevronDown, Info, Loader2, XCircle } from "lucide-react";
import type { ButtonHTMLAttributes, ReactNode } from "react";
import { forwardRef, useState } from "react";

// ------------------------------------------------------------------ Button

type Variant = "primary" | "secondary" | "ghost" | "danger" | "link";
type Size = "sm" | "md";

const variants: Record<Variant, string> = {
  primary: "bg-brand text-white hover:bg-brand-hover border border-brand disabled:opacity-50",
  secondary: "bg-surface text-ink border border-line-strong hover:bg-subtle disabled:opacity-50",
  ghost: "text-ink-2 hover:bg-subtle hover:text-ink border border-transparent",
  danger: "bg-surface text-bad border border-line-strong hover:bg-bad-soft hover:border-bad/40",
  link: "text-brand hover:underline border border-transparent px-0",
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  icon?: ReactNode;
  loading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = "secondary", size = "md", icon, loading, className, children, disabled, type = "button", ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      className={clsx(
        "inline-flex select-none items-center justify-center gap-1.5 whitespace-nowrap rounded font-medium transition-colors disabled:cursor-not-allowed",
        size === "sm" ? "h-7 px-2.5 text-xs" : "h-8 px-3 text-sm",
        variants[variant],
        className,
      )}
      {...rest}
    >
      {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : icon}
      {children}
    </button>
  );
});

export function IconButton({ label, className, ...rest }: ButtonProps & { label: string }) {
  // !px-0: Button's size padding (px-2.5) comes later in the stylesheet and would squeeze the icon to a sliver
  return <Button variant="ghost" size="sm" aria-label={label} title={label} className={clsx("w-7 !px-0", className)} {...rest} />;
}

// ------------------------------------------------------------------ Badge

type Tone = "neutral" | "brand" | "ok" | "warn" | "bad";
const tones: Record<Tone, string> = {
  neutral: "bg-subtle text-ink-2 border-line",
  brand: "bg-brand-soft text-brand border-brand/25",
  ok: "bg-ok-soft text-ok border-ok/25",
  warn: "bg-warn-soft text-warn border-warn/25",
  bad: "bg-bad-soft text-bad border-bad/25",
};

export function Badge({ tone = "neutral", children, className, mono }: { tone?: Tone; children: ReactNode; className?: string; mono?: boolean }) {
  return (
    <span className={clsx("inline-flex h-5 items-center gap-1 whitespace-nowrap rounded border px-1.5 text-2xs font-medium", tones[tone], mono && "font-mono", className)}>
      {children}
    </span>
  );
}

export function StatusDot({ tone }: { tone: Tone }) {
  const color = { neutral: "bg-ink-3", brand: "bg-brand", ok: "bg-ok", warn: "bg-warn", bad: "bg-bad" }[tone];
  return <span className={clsx("inline-block h-2 w-2 shrink-0 rounded-full", color)} />;
}

// ------------------------------------------------------------------ Panels

export function Panel({ title, actions, children, className, bodyClassName, subtitle }: {
  title?: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
  bodyClassName?: string;
}) {
  // clsx doesn't merge Tailwind classes and .p-4 is emitted after .p-0, so a padding class in
  // bodyClassName has to replace the default instead of being appended to it.
  const ownPadding = /(?:^|\s)p[xytrbl]?-/.test(bodyClassName ?? "");
  return (
    <section className={clsx("rounded-md border border-line bg-surface", className)}>
      {(title || actions) && (
        <header className="flex min-h-[40px] items-center justify-between gap-3 border-b border-line px-4 py-2">
          <div className="min-w-0">
            {title && <h2 className="truncate text-sm font-semibold">{title}</h2>}
            {subtitle && <p className="truncate text-xs text-ink-3">{subtitle}</p>}
          </div>
          {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
        </header>
      )}
      <div className={clsx(!ownPadding && "p-4", bodyClassName)}>{children}</div>
    </section>
  );
}

/**
 * Section that folds open/closed by its header. `card` = a standalone panel; otherwise a lighter
 * bordered group to nest inside a panel. Header actions stay clickable without toggling.
 */
export function Collapsible({ title, subtitle, actions, children, defaultOpen = false, card, className, bodyClassName }: {
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  defaultOpen?: boolean;
  card?: boolean;
  className?: string;
  bodyClassName?: string;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className={clsx("rounded-md border border-line", card && "bg-surface", className)}>
      <header className={clsx("flex items-center gap-2", card ? "min-h-[40px] px-4 py-2" : "px-2.5 py-1.5")}>
        <button type="button" aria-expanded={open} onClick={() => setOpen((v) => !v)} className="flex min-w-0 flex-1 items-center gap-2 text-left">
          <ChevronDown className={clsx("h-3.5 w-3.5 shrink-0 text-ink-3 transition-transform", !open && "-rotate-90")} />
          <span className="min-w-0">
            <span className={clsx("block truncate font-semibold", card ? "text-sm" : "text-xs")}>{title}</span>
            {subtitle && <span className="block text-xs text-ink-3">{subtitle}</span>}
          </span>
        </button>
        {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
      </header>
      {open && <div className={clsx("border-t border-line", card ? "p-4" : "p-2.5", bodyClassName)}>{children}</div>}
    </section>
  );
}

export function PageHeader({ title, description, actions, breadcrumb }: {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  breadcrumb?: ReactNode;
}) {
  return (
    <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
      <div className="min-w-0">
        {breadcrumb && <div className="mb-1 text-xs text-ink-3">{breadcrumb}</div>}
        <h1 className="truncate text-xl font-semibold tracking-tight">{title}</h1>
        {description && <p className="mt-0.5 max-w-3xl text-sm text-ink-2">{description}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

export function StatTile({ label, value, hint, tone }: { label: string; value: ReactNode; hint?: ReactNode; tone?: Tone }) {
  return (
    <div className="rounded-md border border-line bg-surface px-4 py-3">
      <div className="text-xs text-ink-2">{label}</div>
      <div className={clsx("mt-1 text-xl font-semibold tabular-nums", tone === "bad" && "text-bad", tone === "warn" && "text-warn")}>{value}</div>
      {hint && <div className="mt-0.5 text-xs text-ink-3">{hint}</div>}
    </div>
  );
}

export function KeyValue({ items, cols = 2 }: { items: [string, ReactNode][]; cols?: 1 | 2 | 3 }) {
  return (
    <dl className={clsx("grid gap-x-6 gap-y-2.5", cols === 1 ? "grid-cols-1" : cols === 2 ? "sm:grid-cols-2" : "sm:grid-cols-3")}>
      {items.map(([k, v]) => (
        <div key={k} className="min-w-0">
          <dt className="text-xs text-ink-3">{k}</dt>
          <dd className="mt-0.5 break-words text-sm">{v ?? "—"}</dd>
        </div>
      ))}
    </dl>
  );
}

// ------------------------------------------------------------------ Feedback

export function Spinner({ className }: { className?: string }) {
  return <Loader2 className={clsx("h-4 w-4 animate-spin text-ink-3", className)} />;
}

export function Loading({ label = "Yükleniyor…" }: { label?: string }) {
  return (
    <div className="flex items-center gap-2 px-4 py-8 text-sm text-ink-3">
      <Spinner /> {label}
    </div>
  );
}

export function EmptyState({ title, description, action, icon }: { title: string; description?: ReactNode; action?: ReactNode; icon?: ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center px-6 py-10 text-center">
      {icon && <div className="mb-2 text-ink-3">{icon}</div>}
      <div className="text-sm font-medium">{title}</div>
      {description && <div className="mt-1 max-w-md text-xs text-ink-3">{description}</div>}
      {action && <div className="mt-3">{action}</div>}
    </div>
  );
}

export function Alert({ tone = "brand", title, children, className }: { tone?: Tone; title?: ReactNode; children?: ReactNode; className?: string }) {
  const Icon = { neutral: Info, brand: Info, ok: CheckCircle2, warn: AlertTriangle, bad: XCircle }[tone];
  return (
    <div className={clsx("flex gap-2.5 rounded border px-3 py-2.5 text-sm", tones[tone], className)}>
      <Icon className="mt-0.5 h-4 w-4 shrink-0" />
      <div className="min-w-0 text-ink">
        {title && <div className="font-medium">{title}</div>}
        {children && <div className={clsx("text-ink-2", title && "mt-0.5")}>{children}</div>}
      </div>
    </div>
  );
}

export function ErrorBox({ error }: { error: unknown }) {
  if (!error) return null;
  const message = error instanceof Error ? error.message : String(error);
  return <Alert tone="bad" title="İşlem başarısız">{message}</Alert>;
}

export function Divider({ className }: { className?: string }) {
  return <hr className={clsx("border-line", className)} />;
}
